import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, StateScreen } from '@cowoo/shared/ui-web';
import { pollOrder, type PollResult } from '@cowoo/shared/checkout';
import { api } from '../../lib/api';
import { useCheckoutStore } from '../../lib/session';
import { env } from '../../env';

/**
 * `/fertig?order=` — the success page (09-web-member § 9).
 *
 * The funnel ends here: everything afterwards happens in the app (D-005), so
 * the page's job is to get the buyer into the app. It polls the order first —
 * Stripe confirmed, the webhook decides — and a reload keeps working from the
 * `order` parameter.
 */
export function Done() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get('order');
  const { items, reset } = useCheckoutStore();
  const [result, setResult] = useState<PollResult | null>(orderId ? null : { status: 'paid' });
  const [attempt, setAttempt] = useState(0);
  const hasContract = items.some((item) => item.type === 'contract');

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    setResult(null);
    void pollOrder(orderId, async (id) => {
      const { data, error } = await api.GET('/orders/{id}', { params: { path: { id } } });
      // A foreign or unknown order has nothing to confirm: back to the start.
      if ((error as { code?: string } | undefined)?.code === 'not_found')
        navigate('/start', { replace: true });
      return data ?? null;
    }).then((outcome) => {
      if (cancelled) return;
      setResult(outcome);
      // § 8.3 — a failed order goes back to Zahlung with its panel.
      if (outcome.status === 'failed' || outcome.status === 'expired') {
        navigate(`/zahlung?failed=${outcome.lastPaymentError ?? outcome.status}`, { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [orderId, attempt, navigate]);

  useEffect(() => {
    if (result?.status === 'paid') reset();
  }, [result, reset]);

  if (result === null) {
    return <StateScreen title={t('member.done.confirming')} />;
  }

  if (result.status === 'timeout') {
    return (
      <StateScreen
        title={t('member.done.timeout')}
        action={<Button onClick={() => setAttempt((value) => value + 1)}>{t('member.done.check')}</Button>}
      />
    );
  }

  if (result.status !== 'paid') return null;

  return (
    <StateScreen
      title={hasContract ? t('member.done.titleContract') : t('member.done.title')}
      body={t('member.done.body')}
      action={
        <div className="flex flex-col items-start gap-3">
          <Button
            onClick={() => {
              window.location.href = `${env.SITE_URL}/app`;
            }}
          >
            {t('member.done.cta')}
          </Button>
          <p className="font-body text-muted text-sm">{t('member.done.appHint')}</p>
        </div>
      }
    />
  );
}
